import { createSkySightAssist } from '../../../packages/domain/src/108-sky-sight-assist.mjs';
import { skyProjectileContact } from '../../../packages/domain/src/80-sky-aim.mjs';
import { skyEnemyWeakpoint } from '../../../packages/domain/src/79-sky-precision.mjs';
import { skyCameraAimPoint } from './163-sky-framed-view.mjs';

/** Injected actual camera and contact authority keep assistance honest. No
 * target is eligible through armor and no corrected ray bends after launch.
 * Camera-relative output is independent of device pixel ratio. */
export function createSkyAssistedSight(T, camera, canvas) {
  const assist = createSkySightAssist();
  let ndc = { x: 0, y: 0 },
    engaged = false;
  function request(state, value, options = {}) {
    if (![value?.x, value?.y].every(Number.isFinite)) return null;
    if (!state) {
      assist.reset();
      engaged = false;
      ndc = { ...value };
      return null;
    }
    camera.updateMatrixWorld(true);
    const targets = [...(state.enemies ?? []), ...(state.boss ? [state.boss] : [])].filter(
      (t) => t.hp > 0 && !t.defeated && t.collisionEnabled !== false,
    );
    const candidates = [];
    const rect = canvas.getBoundingClientRect();
    const level = options.assist ?? 'light';
    const envelope =
      Math.max(12, Math.min(24, Math.min(rect.width, rect.height) * 0.025)) *
      (level === 'light' ? 0.5 : 1);
    for (const target of level === 'off' ? [] : targets) {
      if (target.kind === 'pillar' || target.leviathanWall || target.vulnerable === false) continue;
      if ((target.kind === 'boss' || target.componentId) && target.vulnerable !== true) continue;
      const points =
        target.componentId || target.kind === 'bonus'
          ? [target.assistPoint ?? target]
          : target.kind === 'boss'
            ? (target.weakpoints ?? [target.weakpoint])
            : [skyEnemyWeakpoint(target)];
      for (const point of points) {
        if (!point || point.exposed === false) continue;
        const world = new T.Vector3(point.x, point.y, point.z);
        if (camera.position.distanceTo(world) > 120) continue;
        const screen = world.clone().project(camera);
        if (Math.abs(screen.x) > 1 || Math.abs(screen.y) > 1 || Math.abs(screen.z) > 1) continue;
        // Project first: distant-screen targets need no expensive opaque-hull
        // sweep. Current candidates still check current geometry every sample;
        // retaining a stale visibility cache could aim through a moving jaw.
        if (
          Math.hypot(
            ((screen.x - value.x) * rect.width) / 2,
            ((screen.y - value.y) * rect.height) / 2,
          ) > envelope
        )
          continue;
        const hits = targets
          .map((other) => ({
            other,
            hit: skyProjectileContact(
              other,
              camera.position,
              point,
              other.contactFrame ?? state.flightFrame,
            ),
          }))
          .filter((c) => c.hit)
          .sort(
            (a, b) =>
              camera.position.distanceToSquared(a.hit) - camera.position.distanceToSquared(b.hit),
          );
        if (!hits[0] || hits[0].other !== target || hits[0].hit.region === 'armor') continue;
        candidates.push({ id: `${target.id}:${point.id ?? ''}`, x: screen.x, y: screen.y });
      }
    }
    const result = assist.sample(value, candidates, {
      width: rect.width,
      height: rect.height,
      ...options,
      level,
    });
    // Clamp the real angular correction after screen-space smoothing.
    const ray = (v) =>
      new T.Vector3(v.x, v.y, 0.5).unproject(camera).sub(camera.position).normalize();
    const requested = ray(value),
      corrected = ray(result),
      angle = requested.angleTo(corrected);
    const limit = ((level === 'light' ? 0.75 : 1.5) * Math.PI) / 180;
    if (angle > limit) {
      const axis = new T.Vector3().crossVectors(requested, corrected).normalize();
      const bounded = requested
        .clone()
        .applyAxisAngle(axis, limit)
        .multiplyScalar(30)
        .add(camera.position)
        .project(camera);
      result.x = bounded.x;
      result.y = bounded.y;
    }
    ndc = { x: result.x, y: result.y };
    engaged = result.engaged;
    return skyCameraAimPoint(T, camera, ndc, state);
  }
  return {
    request,
    // Presentation-only point keeps the requested safe reticle at the same
    // normalized pixel when a cinematic camera looks outside the firing frame.
    // Never feed this fallback into the ballistic input/validation boundary.
    displayPoint(state) {
      const physical = skyCameraAimPoint(T, camera, ndc, state);
      if (physical) return physical;
      const direction = new T.Vector3(ndc.x, ndc.y, 0.5)
        .unproject(camera)
        .sub(camera.position)
        .normalize();
      const point = direction.multiplyScalar(20).add(camera.position);
      return { x: point.x, y: point.y, z: point.z };
    },
    reset: () => {
      assist.reset();
      ndc = { x: 0, y: 0 };
      engaged = false;
    },
    get ndc() {
      return ndc;
    },
    get engaged() {
      return engaged;
    },
  };
}
